/**
 * Karbon360° v2 — design-system primitives.
 *
 * These emit `k2-`-prefixed classes defined in styles/karbon-v2-ui.css. Screens
 * from Phase 3 onward are built from these rather than inline styles, which is
 * what lets the old per-page CSS retire.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { X } from '../Icons';
import '../../../styles/karbon-v2-ui.css';

const cx = (...a) => a.filter(Boolean).join(' ');
export const fmt = (n, d = 2) =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? '—'
    : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

/* ── button ──────────────────────────────────────────────────────────── */
export function Button({ variant, size, icon: Icon, iconOnly, children, className, ...rest }) {
  return (
    <button
      type="button"
      className={cx('k2-b',
        variant && `k2-b-${variant}`,
        size && `k2-b-${size}`,
        iconOnly && 'k2-b-ico',
        className)}
      {...rest}
    >
      {Icon && <Icon s={size === 'sm' ? 13 : 14} />}
      {children}
    </button>
  );
}

/* ── card ────────────────────────────────────────────────────────────── */
export function Card({ title, sub, right, footer, dark, children, className, bodyProps }) {
  return (
    <div className={cx('k2-c', dark && 'k2-c-dark', className)}>
      {(title || right) && (
        <div className="k2-c-h">
          <div>
            {title && <div className="k2-t">{title}</div>}
            {sub && <div className="k2-s">{sub}</div>}
          </div>
          {right && <div className="k2-r">{right}</div>}
        </div>
      )}
      <div className="k2-c-b" {...bodyProps}>{children}</div>
      {footer && <div className="k2-c-f">{footer}</div>}
    </div>
  );
}

/* ── page header / tabs ──────────────────────────────────────────────── */
export function PageHeader({ title, desc, children }) {
  return (
    <div className="k2-ph">
      <div>
        <h1>{title}</h1>
        {desc && <p>{desc}</p>}
      </div>
      {children && <div className="k2-ph-act">{children}</div>}
    </div>
  );
}

export function Tabs({ items, value, onChange }) {
  return (
    <div className="k2-tabbar" role="tablist">
      {items.map((t) => (
        <button
          key={t.id}
          role="tab"
          aria-selected={t.id === value}
          className={cx(t.id === value && 'k2-on')}
          onClick={() => onChange(t.id)}
        >
          {t.label}
          {t.count !== undefined && <span className="k2-cnt">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

/* ── badge / data quality / delta ────────────────────────────────────── */
export function Badge({ tone, dot, lg, children }) {
  return (
    <span className={cx('k2-g', tone && `k2-g-${tone}`, lg && 'k2-g-lg')}>
      {dot && <span className="k2-d" />}
      {children}
    </span>
  );
}

const DQ_LABEL = { 3: 'Measured', 2: 'Calculated', 1: 'Estimated' };
/** Evidence tier: 3 = metered/invoiced, 2 = activity × factor, 1 = spend proxy. */
export function DataQuality({ q, showLabel = true }) {
  return (
    <span className="k2-dq" data-q={q} title={`Data quality: ${DQ_LABEL[q] || 'Unknown'}`}>
      <span className="k2-bars"><i /><i /><i /></span>
      {showLabel && (DQ_LABEL[q] || 'Unknown')}
    </span>
  );
}

/**
 * Delta chip. `invert` flips the colour logic: for emissions a RISE is bad, so
 * the default treats up as negative. Green and red are semantic here, never
 * decorative.
 */
export function Delta({ v, invert = false }) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return null;
  const n = Number(v);
  const flat = Math.abs(n) < 0.05;
  const good = invert ? n > 0 : n < 0;

  /**
   * Beyond about a 10× swing a percentage stops communicating: real data in this
   * app produces figures like "+65,460%" when one month holds a single outsized
   * record. Show a multiple instead — "×655" reads as "something is off", which
   * is the honest message, whereas five digits of percent reads as precision we
   * do not have. The threshold is presentational only; the value is unchanged.
   */
  const huge = Math.abs(n) >= 1000;
  const label = huge
    ? `×${Math.abs(1 + n / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : `${Math.abs(n).toFixed(1)}%`;

  return (
    <span
      className={cx('k2-dl', flat ? 'k2-fl' : good ? 'k2-dn' : 'k2-up')}
      title={huge ? `${n > 0 ? '+' : ''}${n.toFixed(1)}% — an unusually large swing, usually a single outsized record` : undefined}
    >
      {!flat && (n > 0 ? <X.ChevU s={11} /> : <X.ChevD s={11} />)}
      {label}
    </span>
  );
}

export function Stat({ label, value, unit, delta, invert, foot, tone, children }) {
  return (
    <div className={cx('k2-st', tone && `k2-${tone}`)}>
      <div className="k2-lb2">{label}</div>
      <div className="k2-v">
        {value}
        {unit && <span className="k2-u">{unit}</span>}
      </div>
      {(foot || delta !== undefined) && (
        <div className="k2-ft">
          {delta !== undefined && <Delta v={delta} invert={invert} />}
          {foot}
        </div>
      )}
      {children}
    </div>
  );
}

/* ── form fields ─────────────────────────────────────────────────────── */
export function Field({ label, required, optional, hint, hintTone, error, children }) {
  return (
    <div className="k2-f">
      {label && (
        <label className="k2-lb">
          {label}
          {required && <span className="k2-rq">*</span>}
          {optional && <span className="k2-opt">optional</span>}
        </label>
      )}
      {children}
      {(hint || error) && (
        <div className={cx('k2-hint', (error || hintTone === 'err') && 'k2-e')}>
          <X.Info s={12} />
          {error || hint}
        </div>
      )}
    </div>
  );
}

export const Input = ({ className, invalid, ...p }) => <input className={cx('k2-in', invalid && 'k2-err', className)} {...p} />;
export const Select = ({ className, children, ...p }) => <select className={cx('k2-se', className)} {...p}>{children}</select>;
export const UnitInput = ({ unit, ...p }) => (
  <div className="k2-unit-in"><Input {...p} /><span className="k2-u">{unit}</span></div>
);
export function SearchInput({ value, onChange, placeholder = 'Search…', ...p }) {
  return (
    <div className="k2-srch">
      <X.Search s={14} />
      <input value={value} onChange={onChange} placeholder={placeholder} {...p} />
    </div>
  );
}

/* ── definition list / section ───────────────────────────────────────── */
export function DList({ rows }) {
  return (
    <dl className="k2-dt">
      {rows.filter(Boolean).map(([k, v], i) => (
        <React.Fragment key={`${k}-${i}`}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

export function Sect({ title, children }) {
  return (
    <section className="k2-sect">
      <h4>{title}</h4>
      {children}
    </section>
  );
}

/**
 * Calculation trace — the product's differentiator, and the one component that
 * must never lie.
 *
 * The prototype hardcoded `÷ 1000` and back-solved the activity from the stored
 * result, which renders a wrong line for any factor that is not expressed per
 * kilogram (the clinker factor is tCO₂e/tonne, so it displayed 1000× off).
 * Here the caller supplies every term explicitly, and the component RE-DERIVES
 * the result and flags a mismatch rather than displaying arithmetic that does
 * not compute.
 */
export function CalcTrace({ activity, activityUnit, factor, factorUnit, divisor = 1000, result, resultUnit = 'tCO₂e', tolerance = 1e-6 }) {
  const derived = (Number(activity) * Number(factor)) / Number(divisor);
  const stated = Number(result);
  const ok = Number.isFinite(derived) && Number.isFinite(stated) && Math.abs(derived - stated) <= Math.max(tolerance, Math.abs(stated) * 1e-6);

  return (
    <div className="k2-calc">
      <span className="k2-lbl">Formula</span>
      <div>
        <span className="k2-vr">{Number(activity).toLocaleString()}</span> <span className="k2-op">{activityUnit}</span>
        <span className="k2-op"> × </span>
        <span className="k2-vr">{factor}</span> <span className="k2-op">{factorUnit}</span>
        {Number(divisor) !== 1 && (<><span className="k2-op"> ÷ </span><span className="k2-vr">{divisor}</span></>)}
        <span className="k2-op"> = </span>
        <span className="k2-rs">{fmt(derived, 6)} {resultUnit}</span>
      </div>
      {!ok && (
        <div style={{ marginTop: 8 }}>
          <Callout tone="err">
            <b>This figure does not reconcile.</b> The stored result is {fmt(stated, 6)} {resultUnit},
            but the factor and activity above produce {fmt(derived, 6)} {resultUnit}.
          </Callout>
        </div>
      )}
    </div>
  );
}

/* ── callout ─────────────────────────────────────────────────────────── */
const CO_ICON = { info: X.Info, ok: X.Check, warn: X.Warn, err: X.Alert };
export function Callout({ tone = 'info', icon, center, children }) {
  const Ic = icon || CO_ICON[tone] || X.Info;
  return (
    <div className={cx('k2-co', `k2-co-${tone}`, center && 'k2-co-c')} role={tone === 'err' ? 'alert' : undefined}>
      <span className="k2-ic"><Ic s={15} /></span>
      <div>{children}</div>
    </div>
  );
}

/* ── empty / skeleton ────────────────────────────────────────────────── */
export function Empty({ icon: Icon = X.Search, title, body, action }) {
  return (
    <div className="k2-emp">
      <div className="k2-ic"><Icon s={20} /></div>
      <h3>{title}</h3>
      {body && <p>{body}</p>}
      {action}
    </div>
  );
}

export const Skeleton = ({ h = 14, w = '100%', style }) => (
  <div className="k2-sk" style={{ height: h, width: w, ...style }} />
);

/* ── overlays ────────────────────────────────────────────────────────── */
function useEscape(onClose) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
}

export function Drawer({ title, sub, badge, onClose, footer, children }) {
  useEscape(onClose);
  return (
    <>
      <div className="k2-dscrim" onClick={onClose} />
      <aside className="k2-dr" role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : 'Details'}>
        <div className="k2-dr-h">
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="k2-t">{title}</div>
            {sub && <div className="k2-s">{sub}</div>}
          </div>
          {badge}
          <Button variant="gh" size="sm" iconOnly icon={X.Close} onClick={onClose} aria-label="Close" />
        </div>
        <div className="k2-dr-b">{children}</div>
        {footer && <div className="k2-dr-f">{footer}</div>}
      </aside>
    </>
  );
}

export function Modal({ title, sub, wide, onClose, footer, children }) {
  useEscape(onClose);
  return (
    <div className="k2-mscrim" onMouseDown={onClose}>
      <div
        className={cx('k2-md', wide && 'k2-md-w')}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : 'Dialog'}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="k2-md-h">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="k2-t">{title}</div>
            {sub && <div className="k2-s">{sub}</div>}
          </div>
          <Button variant="gh" size="sm" iconOnly icon={X.Close} onClick={onClose} aria-label="Close" />
        </div>
        <div className="k2-md-b">{children}</div>
        {footer && <div className="k2-md-f">{footer}</div>}
      </div>
    </div>
  );
}

/* ── toast ───────────────────────────────────────────────────────────── */
const ToastCtx = createContext(null);

export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);

  const toast = useCallback((message, opts = {}) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const tone = opts.tone || 'ok';
    setItems((v) => [...v, { id, message, tone }]);
    setTimeout(() => setItems((v) => v.filter((t) => t.id !== id)), opts.duration || 4000);
  }, []);

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastCtx.Provider value={value}>
      {children}
      {items.length > 0 && (
        <div className="k2-tst-w" role="status" aria-live="polite">
          {items.map((t) => (
            <div key={t.id} className={cx('k2-tst', t.tone === 'err' && 'k2-bad')}>
              <span className="k2-ic">{t.tone === 'err' ? <X.Alert s={15} /> : <X.Check s={15} />}</span>
              {t.message}
            </div>
          ))}
        </div>
      )}
    </ToastCtx.Provider>
  );
}

/** Replaces alert() across the app. Degrades to a no-op outside the provider. */
export function useToast() {
  const ctx = useContext(ToastCtx);
  return ctx || { toast: () => {} };
}

/* ── stepper / bar row ───────────────────────────────────────────────── */
export function Stepper({ steps, current }) {
  return (
    <div className="k2-stp">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          {i > 0 && <div className="k2-stp-ln" />}
          <div className={cx('k2-stp-i', current === i && 'k2-on', current > i && 'k2-dn')}>
            <span className="k2-stp-n">{current > i ? <X.Check s={11} /> : i + 1}</span>
            <span className="k2-stp-t">{s}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

export function BarRow({ name, value, unit, pct, color = 'var(--brand-700)' }) {
  return (
    <div className="k2-brow">
      <span className="k2-nm2">{name}</span>
      <span className="k2-tr"><span style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: color }} /></span>
      <span className="k2-vl">{value}{unit && <span style={{ color: 'var(--n400)', fontWeight: 400 }}> {unit}</span>}</span>
    </div>
  );
}

/* ── table shell ─────────────────────────────────────────────────────── */
export function TableWrap({ toolbar, footer, children, scroll = true }) {
  return (
    <div className="k2-tw">
      {toolbar && <div className="k2-tbar">{toolbar}</div>}
      <div className={scroll ? 'k2-tscroll' : undefined}>{children}</div>
      {footer && <div className="k2-tfoot">{footer}</div>}
    </div>
  );
}

export function SortHeader({ id, label, sort, setSort, right }) {
  const active = sort.key === id;
  return (
    <th
      className={cx('k2-srt', active && 'k2-act', right && 'k2-r')}
      onClick={() => setSort({ key: id, dir: active ? -sort.dir : -1 })}
      aria-sort={active ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}
    >
      <span className="k2-sc">
        {label}
        {active ? (sort.dir === 1 ? <X.ChevU s={11} /> : <X.ChevD s={11} />) : <X.ChevD s={11} />}
      </span>
    </th>
  );
}
